import React, { useState, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import type { CFMonthlyData } from '../../types/campaignFinance';
import { fmtMoney } from '../../utils/campaignFinanceUtils';

interface CFRaisedVsSpentProps {
  raised: CFMonthlyData[];
  spent: CFMonthlyData[];
}

const RAISED_COLOR = 'var(--cf-in)';
const SPENT_COLOR = 'var(--cf-out)';
const GRID_COLOR = 'var(--cf-grid)';
const AXIS_COLOR = 'var(--cf-axis)';

const monthLabel = (ym: string): string => {
  const [y, m] = (ym || '').split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="card p-2 shadow-lg">
      <div className="text-xs font-semibold text-text-primary dark:text-text-dark-primary mb-1">{monthLabel(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-text-secondary dark:text-text-dark-secondary">{p.name}:</span>
          <span className="font-semibold text-text-primary dark:text-text-dark-primary tabular-nums">{fmtMoney(Number(p.value) || 0, { compact: true })}</span>
        </div>
      ))}
    </div>
  );
};

/** Collapsible raised-vs-spent monthly trend — Recharts area+line with hover tooltips. */
const CFRaisedVsSpent: React.FC<CFRaisedVsSpentProps> = ({ raised, spent }) => {
  const [open, setOpen] = useState(false);

  const data = useMemo(() => {
    const set = new Set<string>();
    raised.forEach(r => set.add(r.month));
    spent.forEach(s => set.add(s.month));
    const rMap = new Map(raised.map(r => [r.month, Number(r.total) || 0]));
    const sMap = new Map(spent.map(s => [s.month, Number(s.total) || 0]));
    return Array.from(set).sort().map(month => ({
      month,
      raised: rMap.get(month) || 0,
      spent: sMap.get(month) || 0,
    }));
  }, [raised, spent]);

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3.5 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary">Raised vs spent · monthly</span>
          <div className="flex gap-3.5 text-[11.5px] font-medium">
            <span className="flex items-center gap-1.5 text-cf-in-text"><span className="w-[11px] h-[3px] rounded-sm bg-cf-in" />Raised</span>
            <span className="flex items-center gap-1.5 text-primary-700 dark:text-primary-dark-400"><span className="w-[11px] h-[3px] rounded-sm bg-cf-out" />Spent</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1.5 border-[1.5px] border-border-strong dark:border-border-dark rounded-[9px] px-3 py-1.5 text-xs font-semibold text-primary-700 dark:text-primary-dark-400 bg-white dark:bg-background-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary transition-colors"
        >
          <TrendingUp className="w-3.5 h-3.5" />
          {open ? 'Hide chart' : 'View chart'}
        </button>
      </div>

      {open && (
        <div className="mt-3.5 pt-3.5 border-t border-border-light dark:border-border-dark">
          {data.length === 0 ? (
            <div className="text-[12px] text-accent-400 py-6 text-center">No monthly activity in range.</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <ComposedChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis
                  dataKey="month"
                  tickFormatter={monthLabel}
                  tick={{ fontSize: 10, fill: AXIS_COLOR }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={(v: number) => fmtMoney(v, { compact: true })}
                  tick={{ fontSize: 10, fill: AXIS_COLOR }}
                  axisLine={false}
                  tickLine={false}
                  width={54}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="raised" name="Raised" stroke={RAISED_COLOR} strokeWidth={2.2} fill={RAISED_COLOR} fillOpacity={0.1} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="spent" name="Spent" stroke={SPENT_COLOR} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
};

export default CFRaisedVsSpent;
